import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class CustomValidators {
  static noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    const isWhitespace = (control.value || '').trim().length === 0;
    return isWhitespace ? { whitespace: true } : null;
  }

  static multipleEmailsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Champ vide = valide (sauf si required est ajouté séparément)
      }

      const emails = control.value.split(';').map(email => email.trim());
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Regex standard de validation d'un email

      const allValid = emails.every(email => emailRegex.test(email));

      return allValid ? null : { multipleEmails: true };
    };
  }

  static allowedCharactersForNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // champ vide = valide (à combiner avec un required si nécessaire)
      }

      // Regex pour autoriser : lettres majuscules/minuscules, espaces, accents, tirets
      const regex = /^[a-zA-ZÀ-ÖØ-öø-ÿ \-']+$/;

      if (!regex.test(control.value)) {
        return { invalidCharacters: true };
      }

      return null;
    };
  }

  static allowedCharactersForCompanyNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value || '';

      if (value.length === 0) {
        return null; // champ vide = OK (c'est le rôle de "required" de gérer ça)
      }

      // Lettres (avec accents), chiffres, espaces, tirets, apostrophes, points, underscores, esperluettes, parenthèses et plus
      const regex = /^[a-zA-ZÀ-ÖØ-öø-ÿ0-9 \-'\._&\(\)\+]+$/;

      if (!regex.test(value)) {
        return { invalidCharacters: true };
      }

      return null;
    };
  }

  static ibanValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value || '';

      if (value.length === 0) {
        return null; // champ vide = OK (c'est le rôle de "required" de gérer ça)
      }

      let iban = value.replace(/\s+/g, ''); // On nettoie les espaces

      if (iban.length !== 27) {
        return { ibanInvalidLength: true };
      }

      // Vérification stricte de la structure
      const regex = /^FR\d{2}\d{5}\d{5}[0-9A-Z]{11}\d{2}$/;
      if (!regex.test(iban)) {
        return { ibanInvalidFormat: true };
      }

      // Vérification de la clé de contrôle (mod 97)
      if (!this.isValidIBAN(iban)) {
        return { ibanInvalidChecksum: true };
      }

      return null;
    };
  }

  static isValidIBAN(iban: string): boolean {
    // Pour la vérification, on met la clé à la fin, convertit les lettres en chiffres et on fait le mod 97
    const rearranged = iban.substring(4) + iban.substring(0, 4);
    const replaced = rearranged
      .split('')
      .map(char => {
        const code = char.charCodeAt(0);
        return code >= 65 && code <= 90 ? (code - 55).toString() : char;
      })
      .join('');

    const remainder = BigInt(replaced) % BigInt(97);
    return remainder === BigInt(1);
  }

  static sirenValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.replace(/\s/g, '');

      if (value.length === 0) {
        return null; // champ vide = OK (c'est le rôle de "required" de gérer ça)
      }

      if (!/^\d{9}$/.test(value)) {
        return { invalidSirenFormat: true };
      }

      if (!this.isValidLuhn(value)) {
        return { invalidSirenChecksum: true };
      }

      return null;
    };
  }

  static siretValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.replace(/\s/g, '');

      if (value.length === 0) {
        return null; // champ vide = OK (c'est le rôle de "required" de gérer ça)
      }

      if (!/^\d{14}$/.test(value)) {
        return { invalidSiretFormat: true };
      }

      if (!this.isValidLuhn(value)) {
        return { invalidSiretChecksum: true };
      }

      return null;
    };
  }

  static isValidLuhn(input: string): boolean {
    let sum = 0;
    let isDouble = false;

    for (let i = input.length - 1; i >= 0; i--) {
      // Parcours de droite à gauche
      let digit = parseInt(input[i], 10);

      if (isDouble) {
        // On double un chiffre sur deux (en partant de la droite)
        digit *= 2;
        if (digit > 9) digit -= 9;
      }

      sum += digit;
      isDouble = !isDouble; // Alterner entre simple et double
    }

    return sum % 10 === 0;
  }

  // ✅ Validateur pour empêcher le texte libre dans dataSource
  static validateData(control: AbstractControl) {
    const value = control.value;

    // Si vide, et pas requis → OK
    if (value === null || value === '') {
      return null;
    }

    if (
      typeof value === 'object' &&
      value !== null &&
      ('name' in value || 'label' in value || 'text' in value || 'code' in value)
    ) {
      return null; // OK
    }

    return { invalidData: true }; // KO
  }
}
